// Brief Vague 2 §7 — Page événement publique partageable (route /event/:eventId)
//
// • Aucune auth requise.
// • React Helmet pour les meta tags OG côté client (LinkedIn/Twitter modernes
//   exécutent JS, donc le scraping marche pour la majorité des plateformes).
// • Pour les crawlers legacy (WhatsApp, etc.), partage l'URL backend
//   /api/public/event/{id}/og qui renvoie du HTML brut avec OG meta + redirect.
// • CTA "Réserver" : redirect vers /signup?redirect=/event/{id} si non
//   connecté, sinon ouvre le flow direct via POST /api/calendar/events/{id}/book.
import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, Clock, Users, MapPin, ArrowRight, ExternalLink } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
// Fix Sep 2026 — les datetimes backend sont UTC naïfs : parse via parseUtc
// (sinon new Date() les interprète en heure locale → décalage de 2h à Genève).
import { parseUtc } from '../lib/calendarDate';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDateTime = (iso) => {
  if (!iso) return '';
  const d = parseUtc(iso);
  if (!d || isNaN(d)) return '';
  return d.toLocaleString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const PublicEventPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [booking, setBooking] = useState(false);
  // Brief audit beta Feb 2026 (P0 Bug 3) — explicit confirmation modal after
  // booking, instead of a discret toast. Shape: { status, expertName, title, when }.
  const [bookingConfirm, setBookingConfirm] = useState(null);
  // Brief Jun 17 §2 — auto-trigger booking once when ?book=1 is in the URL.
  const autoBookedRef = useRef(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios
      .get(`${API}/public/event/${eventId}`)
      .then(r => { if (alive) setEvent(r.data); })
      .catch(err => {
        if (!alive) return;
        setError(err?.response?.status === 404 ? 'introuvable' : 'erreur');
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [eventId]);

  // Brief Vague 2 §7 — set document.title imperatively (react-helmet-async 2.x
  // throws "Helmet expects a string as a child of <title>" in some StrictMode
  // edge cases when using JSX expression in <title>{ogTitle}</title>).
  useEffect(() => {
    if (event?.title) {
      document.title = `${event.title} — OpenSapiens`;
    }
    return () => { document.title = 'OpenSapiens'; };
  }, [event]);

  // Brief Vague 2 §7 — Override the site-wide OG meta tags from
  // /app/frontend/public/index.html so that link unfurls (LinkedIn/Twitter/
  // WhatsApp) show the EVENT-specific preview when scraping the route.
  // react-helmet-async 2.x does NOT remove static tags from index.html, so
  // we update them imperatively on mount + restore on unmount.
  useEffect(() => {
    if (!event) return undefined;
    const expertName = (event.expert?.display_name) || 'un expert OpenSapiens';
    const title = `${event.title || 'Événement'} — OpenSapiens`;
    const rawDesc = (event.description || '').slice(0, 180).trim();
    const desc = rawDesc
      ? `${rawDesc} — avec ${expertName} sur OpenSapiens`
      : `Session avec ${expertName} sur OpenSapiens`;
    const url = `${window.location.origin}/event/${eventId}`;
    const image = event.cover_image || event.expert?.avatar
      || `${window.location.origin}/og-image.png`;
    const updates = [
      { attr: 'property', key: 'og:type', value: 'event' },
      { attr: 'property', key: 'og:title', value: title },
      { attr: 'property', key: 'og:description', value: desc },
      { attr: 'property', key: 'og:url', value: url },
      { attr: 'property', key: 'og:image', value: image },
      { attr: 'property', key: 'twitter:title', value: title },
      { attr: 'property', key: 'twitter:description', value: desc },
      { attr: 'property', key: 'twitter:url', value: url },
      { attr: 'property', key: 'twitter:image', value: image },
    ];
    const restored = [];
    updates.forEach(({ attr, key, value }) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        restored.push({ el, prev: null, created: true });
      } else {
        restored.push({ el, prev: el.getAttribute('content'), created: false });
      }
      el.setAttribute('content', value);
    });
    return () => {
      restored.forEach(({ el, prev, created }) => {
        if (created) {
          el.remove();
        } else if (prev !== null) {
          el.setAttribute('content', prev);
        }
      });
    };
  }, [event, eventId]);

  const handleBook = async () => {
    if (!user) {
      // Brief Jun 17 §2 — preserve the ?book=1 intent through the signup flow so
      // the user lands back on the event page and the booking fires once logged in.
      const target = `/event/${eventId}${window.location.search || '?book=1'}`;
      navigate(`/signup?redirect=${encodeURIComponent(target)}`);
      return;
    }
    // Brief Jul 6 2026 P0 §1 — Enforcement strict des paiements :
    //   • event.price > 0 + expert Stripe actif → Stripe Checkout obligatoire
    //   • event.price > 0 + expert Stripe INACTIF → erreur explicite
    //   • event gratuit ou FORCE_FREE_SESSIONS=true → booking direct
    // Le backend applique la même règle en défense-en-profondeur (renvoie 402
    // ou 400 selon le cas). Ici on route le flow côté client pour meilleure UX.
    const isPaid = !!(event?.price && event.price > 0 && !event?.is_free);
    const expertStripeReady = !!event?.expert?.stripe_charges_enabled;
    if (isPaid && !expertStripeReady) {
      toast.error("L'expert n'a pas encore activé les paiements — réservation impossible.");
      return;
    }
    if (isPaid && expertStripeReady) {
      setBooking(true);
      try {
        const { data } = await axios.post(
          `${API}/bookings/${eventId}/checkout`,
          {},
          { withCredentials: true }
        );
        if (data.url) {
          window.location.href = data.url; // Redirect to Stripe Checkout hosted
          return;
        }
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Impossible de lancer le paiement.');
      } finally {
        setBooking(false);
      }
      return;
    }
    setBooking(true);
    try {
      // Brief Phase B2 §5 — sub-slot booking via ?slot=<ISO> for public_series
      // with availability_slots.
      const slotIso = searchParams.get('slot');
      const body = slotIso ? { slot_start_time: slotIso } : {};
      const resp = await axios.post(
        `${API}/calendar/events/${eventId}/book`,
        body,
        { withCredentials: true }
      );
      // Brief audit beta Feb 2026 (P0 Bug 3) — open explicit modal instead of
      // just a toast. Distinguer status confirmed (auto-confirm public) vs
      // pending / pending_payment (attente de validation ou paiement).
      const bookingStatus = resp?.data?.status || 'confirmed';
      const expertName = event?.expert?.display_name || 'l\'expert';
      const whenIso = slotIso || event?.start_time;
      let when = '';
      if (whenIso) {
        try {
          // Brief Jul 6 2026 P0 §2 — defensive : rejeter Invalid Date silently.
          const d = parseUtc(whenIso);
          if (d && !Number.isNaN(d.getTime())) {
            when = d.toLocaleString('fr-FR', {
              weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit',
            });
          }
        } catch { /* ignore */ }
      }
      setBookingConfirm({
        status: bookingStatus,
        expertName,
        title: event?.title || 'la session',
        when,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Réservation impossible');
    } finally {
      setBooking(false);
    }
  };

  // Brief Jun 17 §2 — when the page is loaded with ?book=1, automatically open
  // the booking flow as soon as the event is fetched. Guarded with a ref so the
  // POST never fires twice on re-render. Non-bookable cases (past / full /
  // private) fall back to the regular CTA and the toast surfaces the reason.
  // Brief Phase B2 §5 — when ?slot=<ISO> is present, the parent's `is_past` /
  // capacity flags do not apply (the booking is for a virtual sub-slot in the
  // future). We let the backend enforce the slot-level constraints.
  useEffect(() => {
    if (autoBookedRef.current) return;
    if (!event) return;
    if (searchParams.get('book') !== '1') return;
    const hasSlot = !!searchParams.get('slot');
    if (!hasSlot && event.is_past) return;
    if (!hasSlot && event.max_capacity && event.participants_count >= event.max_capacity) return;
    autoBookedRef.current = true;
    handleBook();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, searchParams]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-gray-900">
        <p className="text-gray-500 dark:text-gray-400" data-testid="public-event-loading">Chargement…</p>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-gray-900 p-6">
        <div className="text-center max-w-md" data-testid="public-event-not-found">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Événement introuvable</h1>
          <p className="text-gray-500 dark:text-gray-400 mb-6">Ce lien n&apos;est plus valide ou l&apos;événement a été annulé.</p>
          <Link to="/" className="text-purple-600 hover:underline">Retour à l&apos;accueil →</Link>
        </div>
      </div>
    );
  }

  const isPrivate = event.type === 'private_1on1';
  // Brief Phase B3 §3 — when ?slot=<ISO> is present, override the displayed
  // date/time with the specific sub-slot the visitor picked. The parent series
  // start_time is the first occurrence (potentially long past); the actual
  // booking will use the slot timestamp.
  const slotIso = searchParams.get('slot');
  const slotStartDate = slotIso ? parseUtc(slotIso) : null;
  const slotEndDate = (slotStartDate && event.duration_minutes)
    ? new Date(slotStartDate.getTime() + event.duration_minutes * 60 * 1000)
    : null;
  const hasValidSlot = slotStartDate && !isNaN(slotStartDate.getTime());
  const displayStartIso = hasValidSlot ? slotStartDate.toISOString() : event.start_time;
  // When a slot is set, isPast should be based on the slot, not the parent.
  const isPast = hasValidSlot
    ? slotStartDate.getTime() < Date.now()
    : !!event.is_past;
  const isFull = !hasValidSlot && event.max_capacity && event.participants_count >= event.max_capacity;
  const expert = event.expert || {};
  const canonicalUrl = `${window.location.origin}/event/${eventId}`;

  // Brief Audit Beta Feb 2026 — Paid CTA when Stripe Connect ready + price>0
  const isPaidBookable = !!(event?.price && event.price > 0 && !event?.is_free && event?.expert?.stripe_charges_enabled);
  const primaryCtaLabel = isPast
    ? 'Voir d\'autres sessions'
    : isFull
      ? 'Complet — Voir d\'autres sessions'
      : isPrivate
        ? 'Demander une session'
        : isPaidBookable
          ? `Payer ${event.price} ${(event.currency || 'CHF').toUpperCase()} →`
          : (event?.price && event.price > 0 && !event?.is_free)
            ? 'Réservation temporairement indisponible'
            : 'Réserver cette session';

  // Brief Jul 6 2026 P0 §3 iter_69 UI polish — désactiver le CTA quand event
  // payant mais expert Stripe pas encore prêt : évite le triple-toast et
  // clarifie l'état pour l'utilisateur.
  const isPaidButNoStripe = !!(event?.price && event.price > 0 && !event?.is_free && !event?.expert?.stripe_charges_enabled);
  const primaryCtaAction = (isPast || isFull || isPaidButNoStripe) ? () => navigate('/app/discover') : handleBook;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-gray-900" data-testid="public-event-page">
      {/* Canonical link via Helmet (the OG/Twitter meta are managed imperatively
          in the useEffect above so that they OVERRIDE the site-wide defaults
          from public/index.html). */}
      <Helmet>
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>

      {/* Top bar — minimal branding for non-auth */}
      <header className="border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-800">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/" className="font-bold text-lg text-gray-900 dark:text-white">OpenSapiens</Link>
          {!user && (
            <Link to={`/login?redirect=/event/${eventId}`} className="text-sm font-medium text-purple-600 hover:underline">
              Se connecter
            </Link>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-8">
        {/* Cover image */}
        {event.cover_image && (
          <img
            src={event.cover_image}
            alt={event.title}
            className="w-full max-h-[280px] object-cover rounded-2xl mb-6"
            data-testid="public-event-cover"
          />
        )}

        {/* Title + status badge */}
        <div className="flex items-start gap-3 mb-4">
          <h1 className="flex-1 text-3xl font-bold text-gray-900 dark:text-white" data-testid="public-event-title">
            {event.title}
          </h1>
          {isPast && (
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200" data-testid="public-event-past-badge">
              Terminé
            </span>
          )}
        </div>

        {/* Meta */}
        <div className="flex flex-wrap items-center gap-4 mb-6 text-sm text-gray-600 dark:text-gray-300">
          <span className="inline-flex items-center gap-1.5" data-testid="public-event-when">
            <Calendar className="h-4 w-4" />
            {hasValidSlot
              ? `${formatDateTime(displayStartIso)}${slotEndDate ? ` — ${slotEndDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}` : ''}`
              : formatDateTime(event.start_time)
            }
          </span>
          {event.duration_minutes && (
            <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" /> {event.duration_minutes} min</span>
          )}
          {!isPrivate && !hasValidSlot && typeof event.participants_count === 'number' && (
            <span className="inline-flex items-center gap-1.5" data-testid="public-event-participants">
              <Users className="h-4 w-4" /> {event.participants_count} inscrit{event.participants_count > 1 ? 's' : ''}
              {event.max_capacity ? ` / ${event.max_capacity}` : ''}
            </span>
          )}
          {hasValidSlot && (
            <span className="inline-flex items-center gap-1.5 text-cyan-600 dark:text-cyan-400" data-testid="public-event-slot-indicator">
              Créneau sélectionné
            </span>
          )}
        </div>

        {/* Description */}
        {event.description && (
          <div className="prose prose-sm dark:prose-invert max-w-none mb-8 whitespace-pre-line text-gray-700 dark:text-gray-200">
            {event.description}
          </div>
        )}

        {/* Expert card */}
        {expert && expert.user_id && (
          <div className="flex items-center gap-3 bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 mb-8" data-testid="public-event-expert-card">
            <img
              src={expert.avatar || `${window.location.origin}/avatar-default.png`}
              alt={expert.display_name}
              className="w-12 h-12 rounded-full object-cover"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-900 dark:text-white truncate">{expert.display_name}</p>
              {expert.tagline && <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{expert.tagline}</p>}
            </div>
            <Link
              to={user ? `/app/profile/${expert.user_id}` : `/signup?redirect=/event/${eventId}`}
              className="text-sm font-semibold text-purple-600 hover:underline whitespace-nowrap"
              data-testid="public-event-expert-cta"
            >
              Voir le profil <ExternalLink className="inline h-3 w-3" />
            </Link>
          </div>
        )}

        {/* Price banner — 3 états : paiement actif Stripe / paiement en cours
            de configuration (expert non onboarded) / gratuit beta.  */}
        {isPaidBookable ? (
          <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-xl p-4 mb-6 text-center" data-testid="public-event-price-banner-paid">
            <p className="text-sm text-purple-700 dark:text-purple-300 font-semibold">
              {event.price} {(event.currency || 'CHF').toUpperCase()} · Paiement sécurisé via Stripe
            </p>
            <p className="text-[11px] text-purple-500/80 dark:text-purple-400/80 mt-1">
              L&apos;expert reçoit le paiement directement sur son compte.
            </p>
          </div>
        ) : event?.price > 0 && !event?.is_free ? (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl p-4 mb-6 text-center" data-testid="public-event-price-banner-payment-pending">
            <p className="text-sm text-amber-700 dark:text-amber-300 font-semibold">
              Paiement en cours de configuration
            </p>
            <p className="text-[11px] text-amber-600/80 dark:text-amber-400/80 mt-1">
              L&apos;expert n&apos;a pas encore activé les paiements. Réservation temporairement indisponible.
            </p>
          </div>
        ) : (
          <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700 rounded-xl p-4 mb-6 text-center" data-testid="public-event-price-banner-free">
            <p className="text-sm text-emerald-700 dark:text-emerald-300 font-semibold">
              Gratuit pendant la beta
            </p>
          </div>
        )}

        {/* Primary CTA */}
        <button
          type="button"
          onClick={primaryCtaAction}
          disabled={booking || isPaidButNoStripe}
          className="w-full inline-flex items-center justify-center gap-2 font-semibold rounded-xl px-6 py-3 transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            background: isPaidButNoStripe ? '#9CA3AF' : (isPrivate ? '#7F77DD' : '#1D9E75'),
            color: '#FFFFFF',
          }}
          title={isPaidButNoStripe ? 'Réservation impossible : l\'expert n\'a pas encore activé les paiements.' : ''}
          data-testid="public-event-book-cta"
        >
          {booking ? 'Réservation…' : primaryCtaLabel} <ArrowRight className="h-4 w-4" />
        </button>

        {!user && !isPast && !isFull && (
          <p className="text-center text-xs text-gray-500 dark:text-gray-400 mt-3">
            Tu seras invité·e à créer un compte gratuit pour confirmer ta place.
          </p>
        )}
      </main>

      {/* Brief audit beta Feb 2026 (P0 Bug 3) — explicit booking confirmation modal */}
      {bookingConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="booking-confirmation-modal">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => { setBookingConfirm(null); navigate('/app/sessions'); }}
          />
          <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md p-6">
            {bookingConfirm.status === 'confirmed' ? (
              <>
                <div className="w-14 h-14 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-4">
                  <svg className="h-8 w-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-2" data-testid="booking-confirm-title">
                  Ta réservation est confirmée !
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-300 text-center mb-2">
                  Tu as réservé « <strong>{bookingConfirm.title}</strong> » avec <strong>{bookingConfirm.expertName}</strong>.
                </p>
                {bookingConfirm.when && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-4">
                    📅 {bookingConfirm.when}
                  </p>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center mb-6">
                  Tu recevras un rappel avant la session. Retrouve-la dans Mes sessions.
                </p>
              </>
            ) : (
              <>
                <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: '#FDE5C8' }}>
                  <svg className="h-8 w-8" style={{ color: '#BA7517' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-2" data-testid="booking-confirm-title">
                  Demande envoyée à {bookingConfirm.expertName}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-300 text-center mb-2">
                  Ta demande pour « <strong>{bookingConfirm.title}</strong> » est <strong style={{ color: '#BA7517' }}>en attente de validation</strong>.
                </p>
                {bookingConfirm.when && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-4">
                    📅 {bookingConfirm.when}
                  </p>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center mb-6">
                  Tu recevras une notification dès que la demande sera acceptée.
                </p>
              </>
            )}
            <button
              type="button"
              onClick={() => { setBookingConfirm(null); navigate('/app/sessions'); }}
              className="w-full inline-flex items-center justify-center gap-2 font-semibold rounded-xl px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white transition-colors"
              data-testid="booking-confirm-ok"
            >
              Voir mes sessions <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicEventPage;
